/* istanbul ignore file */

import { Component, ElementRef, EmbeddedViewRef, inject, Injector, ViewContainerRef, DOCUMENT, input, effect } from '@angular/core';

import { ApContentDSLService } from './dsl/content-dsl.service';
import { CONTENT_COMPONENTS, ContentDSLComponentOutline } from './content.model';
import { ApContentService } from './content.service';

/**
 * @ngdoc component
 * @name ApContentComponent
 * @module ApContentModule
 */
@Component({
    selector: 'ap-content',
    template: '',
    providers: [ApContentDSLService]
})
export class ApContentComponent {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly contentService = inject(ApContentService);
    private readonly contentDslService = inject(ApContentDSLService);
    private readonly injector = inject(Injector);
    private readonly element = inject(ElementRef);

    private readonly viewContainerRef = inject(ViewContainerRef);

    /**
     * Content key to render
     */
    public readonly key = input.required<string>();

    /**
     * Params to pass to content key
     */
    public readonly params = input<{
        [key: string]: string;
    }>();

    private readonly dslPlaceholderNodeString = 'ap-content-dsl-placeholder';
    private readonly dslPlaceholderHtmlString = `<${this.dslPlaceholderNodeString}></${this.dslPlaceholderNodeString}>`;
    private readonly dslItems: { [key: string]: any };

    public constructor() {
        const injectedDslItems = inject(CONTENT_COMPONENTS, { optional: true });

        this.dslItems = injectedDslItems?.reduce((p, c) => ({ ...p, ...c }), {});

        effect(() => this.injectContentEffect());
    }

    private injectContentEffect(): void {
        Array.from(this.element.nativeElement.childNodes).forEach((node: HTMLElement) => node.remove());
        const contentString = this.contentService.get(this.key(), this.params());
        contentString && this.element.nativeElement.appendChild(this.injectContent(contentString, this.injector));
    }

    private injectContent(contentString: string, injector?: Injector): HTMLElement {
        const initialParent = this.document.createElement('div');
        const dslElements = this.contentDslService.parseDSLStrings(contentString);
        let formattedContentString = contentString;
        dslElements.forEach(
            (dslElement) => (formattedContentString = formattedContentString.replace(`[[${dslElement}]]`, this.dslPlaceholderHtmlString))
        );
        initialParent.innerHTML = formattedContentString;
        const placeholderElements = initialParent.querySelectorAll(this.dslPlaceholderNodeString);

        dslElements &&
            dslElements.forEach((value, index) => {
                this.addComponent(value, placeholderElements.item(index) as HTMLElement, injector);
            });
        return initialParent;
    }

    private addComponent(value: string, oldNode: HTMLElement, injector?: Injector): void {
        const componentOutline = this.contentDslService.createComponentOutline(value);
        const { componentName, componentProjectedHtml } = componentOutline;

        const dslItem = this.dslItems?.[componentName];

        // No DSL item, but might be native element
        if (!dslItem) {
            this.addNativeElement(componentOutline, oldNode);
            return;
        }

        const webComponents = ['amountDisplay', 'accordion', 'accordionItem'];
        // DSL is Lit class, but we need to fix the nodeName
        if (webComponents.indexOf(componentName) > -1) {
            componentOutline.componentName = new dslItem().nodeName;
            this.addNativeElement(componentOutline, oldNode);
            return;
        }

        // We create the component and then use its injector as a parent for a child injector
        // This way we can guarantee the injector hierarchy when a child depends on a componentProvider of its parent
        const componentRef = this.viewContainerRef.createComponent(this.dslItems[componentName], {
            injector,
            projectableNodes: [this.getProjectedNodes(componentProjectedHtml)]
        });

        const componentElement = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
        for (const key in componentOutline.componentInputs) {
            if (componentOutline.componentInputs.hasOwnProperty(key)) {
                componentRef.setInput(key, componentOutline.componentInputs[key]);
            }
        }

        oldNode.parentNode.replaceChild(componentElement, oldNode);
    }

    private getProjectedNodes(componentProjectedHtml: string): ChildNode[] {
        const childInjector = Injector.create({ providers: [] });
        return Array.from(this.injectContent(componentProjectedHtml, childInjector).childNodes);
    }

    private addNativeElement(componentOutline: ContentDSLComponentOutline, oldNode: HTMLElement) {
        const { componentName, componentInputs, componentProjectedHtml } = componentOutline;
        const element = this.document.createElement(componentName);

        // Not a registered or native element
        if (element instanceof HTMLUnknownElement) {
            oldNode.remove();
            return;
        }

        this.setAttrs(element, componentInputs);
        this.setChildren(element, componentProjectedHtml);

        oldNode.parentNode.replaceChild(element, oldNode);
    }

    private setAttrs(elm: Node, attrs: { [key: string]: string }): void {
        Object.entries(attrs).forEach(([key, value]) => (elm[key] = value));
    }

    private setChildren(element: Node, componentProjectedHtml: string): void {
        componentProjectedHtml &&
            this.getProjectedNodes(componentProjectedHtml).forEach((child) => {
                element.appendChild(child);
            });
    }
}
